import { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { useResource } from "../lib/hooks";
import { api } from "../lib/api";
import { Modal } from "../components/Modal";
import { InitialsAvatar } from "../components/InitialsAvatar";
import { useAuth } from "../context/AuthContext";
import type { Employee } from "../lib/types";

/**
 * Smart groups, prototype-parity: live rule-based segments over the workforce.
 *
 * Built-in groups (all staff, nurses, compliance bands, not-yet-joined) are
 * hardcoded predicates; custom groups come from /smart-groups, whose criteria
 * (roles / teams / compliance / employment types) are evaluated HERE at view
 * time — derived, not stored, so membership can never go stale. Clicking a
 * card expands the full member list; "+ New smart group" opens a real builder
 * (the prototype's button was a coming-soon stub — this one works).
 */

type Standing = "ok" | "expiring_soon" | "expired";
type DocStatus = "none" | "current" | "expiring" | "expired";
interface ComplianceData { documents: Array<{ employee_id: string; status: DocStatus }>; }
const SEVERITY: Record<DocStatus, number> = { expired: 3, expiring: 2, current: 1, none: 0 };

/** Employees carry team/role ids at runtime for admin+; the shared type omits them. */
type Emp = Employee & { team_id?: string | null; primary_job_role_id?: string | null };
interface NamedRow { id: string; name: string; }
interface InviteRow { id: string; email: string; expired: boolean; }
interface GroupRow {
    id: string; name: string; description: string | null; sort_order: number; is_active: boolean;
    criteria: { role_ids?: string[]; team_ids?: string[]; compliance?: "ok" | "warn" | "bad" | null;
        employment_types?: string[] } | null;
}

interface Card {
    key: string;
    name: string;
    description: string;
    tone: "pass" | "warn" | "block" | "info";
    members: Emp[];
    custom?: GroupRow;
}

const useCompliance = (): Map<string, Standing> => {
    const data = useResource<ComplianceData>("/compliance/documents");
    return useMemo(() => {
        const worst = new Map<string, DocStatus>();
        for (const d of data.data?.documents ?? []) {
            const seen = worst.get(d.employee_id);
            if (!seen || SEVERITY[d.status] > SEVERITY[seen]) worst.set(d.employee_id, d.status);
        }
        const out = new Map<string, Standing>();
        for (const [id, status] of worst) {
            out.set(id, status === "expired" ? "expired" : status === "expiring" ? "expiring_soon" : "ok");
        }
        return out;
    }, [data.data]);
};

const isActive = (e: Emp) => e.employment_status === "active";

export const SmartGroups = () => {
    const { user } = useAuth();
    const canManage = user?.role === "admin" || user?.role === "manager";

    const employees = useResource<Emp[]>("/employees");
    const roles = useResource<NamedRow[]>("/roles");
    const teams = useResource<NamedRow[]>("/teams");
    const invites = useResource<InviteRow[]>(canManage ? "/invites" : null);
    const custom = useResource<GroupRow[]>("/smart-groups");
    const compliance = useCompliance();

    const [expanded, setExpanded] = useState<string | null>(null);
    const [builder, setBuilder] = useState<GroupRow | "new" | null>(null);

    const roleName = useMemo(() => new Map((roles.data ?? []).map((r) => [r.id, r.name])), [roles.data]);
    const teamName = useMemo(() => new Map((teams.data ?? []).map((t) => [t.id, t.name])), [teams.data]);
    const nurseRoleIds = useMemo(
        () => new Set((roles.data ?? []).filter((r) => /nurse|(\b|\()(RN|EN|CN|CNC|CNS|CNK|NP)(\b|\))/i.test(r.name)).map((r) => r.id)),
        [roles.data]);
    const invitedEmails = useMemo(
        () => new Set((invites.data ?? []).filter((i) => !i.expired).map((i) => i.email.toLowerCase())),
        [invites.data]);

    const cards: Card[] = useMemo(() => {
        const standingOf = (e: Emp): Standing => compliance.get(e.id) ?? "ok";
        const staff = (employees.data ?? []).filter(isActive);
        const builtIns: Card[] = [
            { key: "all", name: "All staff", tone: "info",
              description: "Everyone active — auto-maintained", members: staff },
            { key: "nurses", name: "Nurses — RN & EN", tone: "info",
              description: "AHPRA-registered nursing staff",
              members: staff.filter((e) => e.primary_job_role_id != null && nurseRoleIds.has(e.primary_job_role_id)) },
            { key: "warn", name: "Compliance expiring < 60 days", tone: "warn",
              description: "A credential expires soon — chase renewals before rostering",
              members: staff.filter((e) => standingOf(e) === "expiring_soon") },
            { key: "bad", name: "Expired — do not roster", tone: "block",
              description: "At least one expired credential. Blocked from new shifts",
              members: staff.filter((e) => standingOf(e) === "expired") },
            { key: "ok", name: "Fully compliant", tone: "pass",
              description: "Every credential current",
              members: staff.filter((e) => standingOf(e) === "ok") },
            { key: "notjoined", name: "Haven't joined the app", tone: "warn",
              description: "Invite sent, account not yet activated — resend or call",
              members: staff.filter((e) => e.email != null && invitedEmails.has(e.email.toLowerCase())) },
        ];
        const customs: Card[] = (custom.data ?? []).filter((g) => g.is_active).map((g) => {
            const c = g.criteria ?? {};
            const standingWanted = c.compliance === "warn" ? "expiring_soon"
                : c.compliance === "bad" ? "expired" : c.compliance === "ok" ? "ok" : null;
            return {
                key: `custom-${g.id}`, name: g.name, tone: "info" as const,
                description: g.description ?? "Custom group",
                custom: g,
                members: staff.filter((e) =>
                    (!c.role_ids?.length || (e.primary_job_role_id != null && c.role_ids.includes(e.primary_job_role_id))) &&
                    (!c.team_ids?.length || (e.team_id != null && c.team_ids.includes(e.team_id))) &&
                    (!c.employment_types?.length || c.employment_types.includes(e.employment_type)) &&
                    (!standingWanted || standingOf(e) === standingWanted)
                ),
            };
        });
        return [...builtIns, ...customs];
    }, [employees.data, custom.data, compliance, nurseRoleIds, invitedEmails]);

    const removeGroup = async (g: GroupRow) => {
        await api.delete(`/smart-groups/${g.id}`);
        custom.reload();
    };

    return (
        <div>
            <div className="toolbar" style={{ marginBottom: "0.6rem", padding: 0 }}>
                <span className="muted">Live segments — membership updates automatically from user data</span>
                <div className="spacer" />
                {canManage && (
                    <button className="btn" onClick={() => setBuilder("new")}>＋ New smart group</button>
                )}
            </div>

            <div className="grid cols-3">
                {cards.map((g) => {
                    const open = expanded === g.key;
                    return (
                        <section key={g.key} className="card clickable" onClick={() => setExpanded(open ? null : g.key)}>
                            <div className="card-head">
                                <h2>{g.name}</h2>
                                <span className={`chip ${g.tone === "info" ? "mute" : g.tone}`}>{g.members.length}</span>
                            </div>
                            <div className="card-body">
                                <p className="muted">{g.description}</p>
                                {!open ? (
                                    <div className="member-strip">
                                        {g.members.slice(0, 8).map((m) => (
                                            <InitialsAvatar key={m.id} name={m.full_name} />
                                        ))}
                                        {g.members.length > 8 && <span className="muted">+{g.members.length - 8} more</span>}
                                        {g.members.length === 0 && <span className="muted">No one right now.</span>}
                                    </div>
                                ) : (
                                    <div onClick={(e) => e.stopPropagation()}>
                                        {g.members.map((m) => (
                                            <div key={m.id} className="person-row">
                                                <InitialsAvatar name={m.full_name} size={26} />
                                                <div className="person-main">
                                                    <div className="name">
                                                        <Link to={`/employees?id=${m.id}`}>{m.full_name}</Link>
                                                    </div>
                                                    <div className="sub">
                                                        {m.primary_job_role_id ? roleName.get(m.primary_job_role_id) ?? "—" : "—"}
                                                        {m.team_id && teamName.get(m.team_id) ? ` · ${teamName.get(m.team_id)}` : ""}
                                                    </div>
                                                </div>
                                            </div>
                                        ))}
                                        {g.members.length === 0 && <p className="empty">No members match right now.</p>}
                                        {canManage && g.custom && (
                                            <div className="row-actions" style={{ marginTop: "0.5rem" }}>
                                                <button className="icon-btn" onClick={() => setBuilder(g.custom!)}>Edit</button>
                                                <button className="icon-btn danger" onClick={() => void removeGroup(g.custom!)}>Delete</button>
                                            </div>
                                        )}
                                    </div>
                                )}
                            </div>
                        </section>
                    );
                })}
            </div>

            {builder && (
                <GroupBuilder
                    group={builder === "new" ? null : builder}
                    roles={roles.data ?? []}
                    teams={teams.data ?? []}
                    onClose={() => setBuilder(null)}
                    onSaved={() => { setBuilder(null); custom.reload(); }}
                />
            )}
        </div>
    );
};

/** The "+ New smart group" builder — name, description and live-rule filters. */
function GroupBuilder({ group, roles, teams, onClose, onSaved }: {
    group: GroupRow | null;
    roles: NamedRow[];
    teams: NamedRow[];
    onClose: () => void;
    onSaved: () => void;
}) {
    const [name, setName] = useState(group?.name ?? "");
    const [description, setDescription] = useState(group?.description ?? "");
    const [roleIds, setRoleIds] = useState<string[]>(group?.criteria?.role_ids ?? []);
    const [teamIds, setTeamIds] = useState<string[]>(group?.criteria?.team_ids ?? []);
    const [complianceF, setComplianceF] = useState(group?.criteria?.compliance ?? "");
    const [empTypes, setEmpTypes] = useState<string[]>(group?.criteria?.employment_types ?? []);
    const [busy, setBusy] = useState(false);
    const [error, setError] = useState<string | null>(null);

    const toggle = (list: string[], set: (v: string[]) => void, v: string) =>
        set(list.includes(v) ? list.filter((x) => x !== v) : [...list, v]);

    const save = async () => {
        if (!name.trim()) { setError("A group name is required."); return; }
        setBusy(true); setError(null);
        try {
            const body = {
                name: name.trim(), description: description.trim() || null,
                criteria: {
                    ...(roleIds.length ? { role_ids: roleIds } : {}),
                    ...(teamIds.length ? { team_ids: teamIds } : {}),
                    ...(complianceF ? { compliance: complianceF as "ok" | "warn" | "bad" } : {}),
                    ...(empTypes.length ? { employment_types: empTypes } : {}),
                },
            };
            if (group) await api.put(`/smart-groups/${group.id}`, body);
            else await api.post("/smart-groups", body);
            onSaved();
        } catch (err) {
            setError(err instanceof Error ? err.message : "Could not save the group");
        } finally {
            setBusy(false);
        }
    };

    return (
        <Modal
            title={group ? `Edit ${group.name}` : "New smart group"}
            subtitle="Members are matched live from these rules — the group maintains itself."
            onClose={onClose}
            footer={
                <>
                    <button className="btn" disabled={busy} onClick={() => void save()}>
                        {busy ? "Saving…" : group ? "Save group" : "Create group"}
                    </button>
                    <button className="btn ghost" disabled={busy} onClick={onClose}>Cancel</button>
                </>
            }
        >
            {error && <div className="error-banner">{error}</div>}
            <div className="form-grid">
                <div className="field half">
                    <label>Group name <span className="req">*</span></label>
                    <input value={name} onChange={(e) => setName(e.target.value)} placeholder="Support Workers — SIL" />
                </div>
                <div className="field half">
                    <label>Compliance filter</label>
                    <select value={complianceF ?? ""} onChange={(e) => setComplianceF(e.target.value as "" | "ok" | "warn" | "bad")}>
                        <option value="">Any compliance</option>
                        <option value="ok">Fully compliant only</option>
                        <option value="warn">Expiring soon only</option>
                        <option value="bad">Expired only</option>
                    </select>
                </div>
                <div className="field">
                    <label>Description</label>
                    <input value={description} onChange={(e) => setDescription(e.target.value)}
                        placeholder="What this group is for" />
                </div>
            </div>

            <div className="field">
                <label>Roles (any of — blank = all roles)</label>
                <div className="check-grid">
                    {roles.map((r) => (
                        <label className="check" key={r.id}>
                            <input type="checkbox" checked={roleIds.includes(r.id)}
                                onChange={() => toggle(roleIds, setRoleIds, r.id)} /> {r.name}
                        </label>
                    ))}
                </div>
            </div>
            <div className="field">
                <label>Teams (any of — blank = all teams)</label>
                <div className="check-grid">
                    {teams.map((t) => (
                        <label className="check" key={t.id}>
                            <input type="checkbox" checked={teamIds.includes(t.id)}
                                onChange={() => toggle(teamIds, setTeamIds, t.id)} /> {t.name}
                        </label>
                    ))}
                </div>
            </div>
            <div className="field">
                <label>Employment types (any of — blank = all)</label>
                <div className="check-grid">
                    {[["full_time", "Full-time"], ["part_time", "Part-time"], ["casual", "Casual"], ["contractor", "Contractor"]].map(([v, l]) => (
                        <label className="check" key={v}>
                            <input type="checkbox" checked={empTypes.includes(v)}
                                onChange={() => toggle(empTypes, setEmpTypes, v)} /> {l}
                        </label>
                    ))}
                </div>
            </div>
        </Modal>
    );
}
